import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import { Range, Editor, Node, Path } from 'slate';
import isHotKey from 'is-hotkey';
import { SfEditor } from '../custom/custom';
import setEventTransfer from '../custom/set-event-transfer';
import MarkUtils from './mark-utils';
import BlockElementUtils from './block-element-utils';
import InlineElementUtils from './inline-element-utils';
import ListUtils from '../editor-utils/block-element-utils/list-utils';
import TableUtils from '../editor-utils/block-element-utils/table-utils';
import CodeUtils from '../editor-utils/block-element-utils/code-utils';
import FormulaUtils from '../editor-utils/block-element-utils/formula-utils';
import BlockquoteUtils from '../editor-utils/block-element-utils/blockquote-utils';
var EditorUtils = /*#__PURE__*/function () {
  function EditorUtils(_editor) {
    var _this = this;
    _classCallCheck(this, EditorUtils);
    this.onKeyDown = function (event) {
      if (event.nativeEvent.keyCode === 13) {
        event.preventDefault();
        if (isHotKey('shift+enter', event)) {
          if (_this.listUtils.isInlist()) {
            _this.editor.exec({
              type: 'split_list_item'
            });
          }
        } else if (isHotKey('mod+enter', event)) {
          if (_this.codeUtils.isInCodeBlock()) {
            _this.editor.exec({
              type: 'exit_code_block'
            });
          }
          if (_this.tableUtils.isInTable()) {
            _this.editor.exec({
              type: 'exit_table'
            });
          }
        } else {
          _this.editor.exec({
            type: 'insert_break'
          });
        }
      }
      if (isHotKey('shift+tab', event)) {
        event.preventDefault();
        if (_this.listUtils.isInlist()) {
          var _Editor$nodes = Editor.nodes(_this.editor, {
              match: [{
                type: 'unordered_list'
              }, {
                type: 'ordered_list'
              }],
              at: _this.editor.selection,
              mode: 'highest'
            }),
            _Editor$nodes2 = _slicedToArray(_Editor$nodes, 1),
            listNode = _Editor$nodes2[0];
          if (!listNode) return;
          var type = listNode[0].type;
          _this.editor.exec({
            type: "unwrap_".concat(type)
          });
        }
        if (_this.tableUtils.isInTable()) {
          _this.editor.exec({
            type: 'focus_previous_table_cell'
          });
        }
      }
      if (isHotKey('mod+s', event)) {
        event.preventDefault();
        _this.editor.onSave && _this.editor.onSave();
      }
      if (isHotKey('tab', event) && Range.isCollapsed(_this.editor.selection)) {
        event.preventDefault();
        if (_this.codeUtils.isInCodeBlock()) {
          _this.editor.exec({
            type: 'increase_code_block_indent'
          });
        }
        if (_this.listUtils.isInlist()) {
          _this.editor.exec({
            type: 'increase_list_item_depth'
          });
        }
        if (_this.tableUtils.isInTable()) {
          _this.editor.exec({
            type: 'focus_next_table_cell'
          });
        }
      }
      if (isHotKey('mod+b', event)) {
        event.preventDefault();
        if (!_this.codeUtils.isInCodeBlock()) {
          _this.editor.exec({
            type: 'format_text',
            properties: {
              BOLD: true
            }
          });
        }
      }
      if (isHotKey('mod+i', event)) {
        event.preventDefault();
        if (!_this.codeUtils.isInCodeBlock()) {
          _this.editor.exec({
            type: 'format_text',
            properties: {
              ITALIC: true
            }
          });
        }
      }
      if (isHotKey('mod+/', event)) {
        event.preventDefault();
        if (!_this.codeUtils.isInCodeBlock()) {
          _this.editor.exec({
            type: 'format_text',
            properties: {
              CODE: true
            }
          });
        }
      }
      if (isHotKey('mod+4', {
        byKey: true
      })(event)) {
        event.preventDefault();
        var toolbarRef = window.abcdef_editorToolbar;
        toolbarRef.onToggleFormulaDialog();
        return;
      }

      // compate chrome: in chrome press backspace would not exec delete_backward command
      if (isHotKey('backspace', event)) {
        var voidNode = SfEditor.match(_this.editor, _this.editor.selection, 'void');
        if (voidNode && (voidNode[0].type === 'image' || voidNode[0].type === 'formula')) {
          event.preventDefault();
          _this.editor.exec({
            type: 'delete_backward',
            unit: 'character'
          });
          return;
        }
      }
      if (isHotKey('mod+a', event)) {
        if (_this.codeUtils.isInCodeBlock()) {
          var anchorPoint = _this.editor.selection.anchor;
          var focusPoint = _this.editor.selection.focus;
          var anchorCodeBlock = Editor.match(_this.editor, anchorPoint, {
            type: 'code_block'
          });
          var focusCodeBlock = Editor.match(_this.editor, focusPoint, {
            type: 'code_block'
          });
          if (anchorCodeBlock && focusCodeBlock && Path.equals(focusCodeBlock[1], anchorCodeBlock[1])) {
            event.preventDefault();
            Editor.select(_this.editor, focusCodeBlock[1]);
            return;
          }
        }
      }
    };
    this.onCut = function (event, editor) {
      var image = SfEditor.match(_this.editor, _this.editor.selection, {
        type: 'image'
      });
      if (image) {
        // write the image element to clipboard
        setEventTransfer(event, 'fragment', [{
          type: 'paragraph',
          children: [image[0]]
        }]);
        // remove the image element when cut an image due to slate did not has the action when cut a image
        _this.editor.exec({
          type: 'delete_backward',
          unit: 'character'
        });
        return;
      }
    };
    this.onCopy = function (event, editor) {
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();
      if (_this.tableUtils.isInTable()) {
        event.preventDefault();
        var table = _this.tableUtils.getSelectedTableCells();
        if (table) {
          setEventTransfer(event, 'fragment', table);
          return;
        }
      }
      if (editor.selection && !Range.isCollapsed(editor.selection)) {
        // get block deepest block node. 
        // for example: code line in a code block, paragraph in a list
        var blockList = Editor.nodes(editor, {
          at: editor.selection,
          match: 'block'
        });
        if (blockList) {
          var nodeList = Array.from(blockList);
          /**
           * if only one block element selected, write the selected content as paragraph or text to clipboard
           * instead of use slate default behavior which write the outest element to clipboard
          */
          if (nodeList.length === 1) {
            event.preventDefault();
            var _nodeList$ = _slicedToArray(nodeList[0], 2),
              node = _nodeList$[0],
              path = _nodeList$[1];
            path = path.slice(1);
            if (node && node.type === 'paragraph') {
              // get selected paragraph node in selection and write to clipboard
              var fragment = Node.fragment(editor, editor.selection);
              var selectedNode = Node.get(fragment[0], Array(path.length).fill(0));
              setEventTransfer(event, 'fragment', [selectedNode]);
            } else {
              setEventTransfer(event, 'text', SfEditor.text(editor, editor.selection));
            }
            return;
          }
        }
      }
    };
    this.clickMark = function (type) {
      _this.markUtils.setMark(type);
    };
    this.clickHeader = function (type) {
      _this.editor.exec({
        type: 'set_header',
        headerType: type
      });
    };
    this.clickBlock = function (type) {
      if (type === 'blockquote') {
        if (_this.blockquoteUtils.isInBlockquote()) {
          _this.editor.exec({
            type: 'unwrap_blockquote'
          });
        } else {
          _this.editor.exec({
            type: 'set_blockquote'
          });
        }
      }
      if (type === 'code_block') {
        if (_this.codeUtils.isInCodeBlock()) {
          _this.editor.exec({
            type: 'unwrap_code_block'
          });
        } else {
          _this.editor.exec({
            type: 'set_code_block'
          });
        }
      }
      if (type === 'ordered_list' || type === 'unordered_list') {
        if (_this.listUtils.isInlist()) {
          var items = _this.listUtils.getCurrentListItem();
          var item = items[0];
          var currentList = Node.parent(_this.editor, item[1]);
          if (currentList.type === type) {
            if (typeof item[0].data.checked === 'boolean') {
              _this.editor.exec({
                type: 'unwrap_check_list_item'
              });
              return;
            }
            _this.editor.exec({
              type: "unwrap_".concat(currentList.type)
            });
          } else {
            var currentListPath = Path.parent(item[1]);
            var listAncesstor = Node.get(_this.editor, Path.parent(currentListPath));
            // modify the sublist to different types
            if (listAncesstor.type && listAncesstor.type.includes('list')) {
              _this.editor.exec({
                type: 'modify_sublist_type',
                target_list_type: type
              });
              return;
            }
            _this.editor.exec({
              type: "unwrap_".concat(currentList.type)
            });
            _this.editor.exec({
              type: "set_".concat(type)
            });
          }
        } else {
          _this.editor.exec({
            type: "set_".concat(type)
          });
        }
      }
    };
    this.clearFormat = function () {
      _this.editor.exec({
        type: 'clear_format'
      });
    };
    this.unwrapLink = function () {
      _this.inlineElementUtils.unwrapLink();
    };
    this.isLinkActive = function () {
      return _this.inlineElementUtils.isLinkActive();
    };
    this.getLinkText = function (linkNode) {
      return _this.inlineElementUtils.getLinkText(linkNode);
    };
    this.insertLink = function (elementData) {
      return _this.inlineElementUtils.insertLink(elementData);
    };
    this.setLink = function (elementData) {
      return _this.inlineElementUtils.setLink(elementData);
    };
    this.getSelectedText = function () {
      var selection = window.getSelection();
      var range = selection.getRangeAt(0);
      return range.toString();
    };
    this.getCurrentLinkNode = function () {
      return _this.inlineElementUtils.getCurrentLinkNode();
    };
    this.clickCheckItem = function () {
      if (_this.listUtils.isInlist()) {
        var items = _this.listUtils.getCurrentListItem();
        var item = items[0];
        if (typeof item[0].data.checked === 'boolean') {
          _this.editor.exec({
            type: 'unwrap_check_list_item'
          });
          return;
        }
      }
      _this.editor.exec({
        type: 'wrap_check_list_item'
      });
    };
    this.addTable = function (rowCount, columnCount) {
      _this.editor.exec({
        type: 'insert_table',
        data: {
          rowCount: rowCount,
          columnCount: columnCount
        }
      });
    };
    this.addFormula = function (data) {
      _this.editor.exec({
        type: 'insert_formula',
        data: data
      });
    };
    this.setFormula = function (data) {
      _this.editor.exec({
        type: 'set_formula',
        data: data
      });
    };
    this.addCustomColumn = function () {
      _this.editor.exec({
        type: 'insert_seatable_column'
      });
    };
    this.insertImage = function (data) {
      _this.inlineElementUtils.insertImage(data);
    };
    this.removeTable = function () {
      _this.editor.exec({
        type: 'remove_table'
      });
    };
    this.insertColumn = function () {
      _this.editor.exec({
        type: 'insert_column'
      });
    };
    this.removeColumn = function () {
      _this.editor.exec({
        type: 'remove_column'
      });
    };
    this.insertRow = function () {
      _this.editor.exec({
        type: 'insert_row'
      });
    };
    this.removeRow = function () {
      _this.editor.exec({
        type: 'remove_row'
      });
    };
    this.setAlign = function (align) {
      _this.editor.exec({
        type: 'set_table_cell_align',
        align: align
      });
    };
    this.adjustImageSize = function (options) {
      return _this.inlineElementUtils.adjustImageSize(options);
    };
    this.editor = _editor;
    this.markUtils = new MarkUtils(_editor);
    this.blockElementUtils = new BlockElementUtils(_editor);
    this.inlineElementUtils = new InlineElementUtils(_editor);
    this.listUtils = new ListUtils(_editor);
    this.tableUtils = new TableUtils(_editor);
    this.codeUtils = new CodeUtils(_editor);
    this.blockquoteUtils = new BlockquoteUtils(_editor);
    this.formulaUtils = new FormulaUtils(_editor);
    this.onSave = _editor.onSave;
  }
  _createClass(EditorUtils, [{
    key: "getToolbarStatus",
    value:
    // set is Active of each button
    function getToolbarStatus() {
      var selection = this.editor.selection;
      var toolbarStatus = {
        headerType: 'paragraph'
      };
      if (!selection) return toolbarStatus;
      var activeHeader = this.blockElementUtils.getActiveHeader([{
        type: 'paragraph'
      }, {
        type: 'header_one'
      }, {
        type: 'header_two'
      }, {
        type: 'header_three'
      }, {
        type: 'header_four'
      }, {
        type: 'header_five'
      }, {
        type: 'header_six'
      }]);
      var isLinkActive = this.isLinkActive();
      var isBlockquoteActive = this.blockquoteUtils.isInBlockquote();
      var isCodeActive = this.codeUtils.isInCodeBlock();
      var isTableActive = this.tableUtils.isInTable();
      var block = activeHeader[0];
      var activeMarks = this.markUtils.getActiveMarks();
      var isCheckListActive = false;
      var isOrderedListActive = false;
      var isUnorderedListActive = false;
      var isSelectedInList = this.listUtils.isInlist();
      if (isSelectedInList) {
        var items = this.listUtils.getCurrentListItem();
        var item = items[0];
        if (item[0].data && typeof item[0].data.checked === 'boolean') {
          isCheckListActive = true;
          isUnorderedListActive = false;
        } else {
          var currentList = Node.parent(this.editor, item[1]);
          var listType = currentList.type;
          if (listType === 'unordered_list') {
            isUnorderedListActive = true;
          } else {
            isOrderedListActive = true;
          }
        }
      }
      toolbarStatus = {
        headerType: block.type,
        isBoldActive: activeMarks.BOLD,
        isItalicActive: activeMarks.ITALIC,
        isInlineCodeActive: activeMarks.CODE,
        isLinkActive: isLinkActive,
        isBlockquoteActive: isBlockquoteActive,
        isOrderedListActive: isOrderedListActive,
        isUnorderedListActive: isUnorderedListActive,
        isCheckListActive: isCheckListActive,
        isCodeActive: isCodeActive,
        isTableActive: isTableActive
      };
      var isFormulaActive = false;
      if (window.canInsertFormula) {
        isFormulaActive = this.formulaUtils.isFormulaActive();
        toolbarStatus.isFormulaActive = isFormulaActive;
      }
      var isActiveColumn = this.inlineElementUtils.isColumnActive();
      toolbarStatus.isColumnActive = isActiveColumn;
      return toolbarStatus;
    }
  }]);
  return EditorUtils;
}();
export { EditorUtils };